import { useQuery } from "@tanstack/react-query";
import fetcher from "@/shared/api/fetcher";
import { isCapabilityEnabled } from "@/api";
import { editorContextKeys } from "./queryKeys";
import type { EditorContext } from "../model/types";
import { readEditorMaterials } from "../model/materials";

/**
 * Data hooks for the endpoints the contract request proposes. Each is gated
 * on its capability: while backend hasn't shipped the route, the query never
 * runs (no doomed request, no 404 noise) and callers get `unavailable: true`
 * to render their own "not yet" state. The interceptor gate is the backstop;
 * this is the polite layer above it.
 */
export function useEditorContext() {
  const enabled = isCapabilityEnabled("context");
  const query = useQuery({
    queryKey: editorContextKeys.context(),
    queryFn: () =>
      fetcher<{ data: EditorContext }>("/context").then((r) => r.data),
    enabled,
    meta: { silent: true },
  });
  return { ...query, unavailable: !enabled };
}

export function useEditorCategories() {
  const enabled = isCapabilityEnabled("browse");
  const query = useQuery({
    queryKey: editorContextKeys.categories(),
    queryFn: () => fetcher<unknown>("/categories"),
    enabled,
    meta: { silent: true },
  });
  return { ...query, unavailable: !enabled };
}

/**
 * The store's ACTIVE materials, in the order the dashboard keeps them — the
 * same set and order `catalog.materials` resolves (backend
 * `resolveMaterials`). `data` is the parsed list; a response in any other
 * shape reads as no list at all, never as an empty one, so a contract slip
 * cannot tell a merchant their store has no materials.
 */
export function useEditorMaterials({
  /** Off for a template with nothing drawn from materials: no request. */
  needed = true,
}: { needed?: boolean } = {}) {
  const enabled = isCapabilityEnabled("materials");
  const query = useQuery({
    queryKey: editorContextKeys.materials(),
    queryFn: () => fetcher<unknown>("/materials").then(readEditorMaterials),
    enabled: enabled && needed,
    meta: { silent: true },
  });
  return { ...query, unavailable: !enabled };
}

export function useEditorProducts(
  params: { search?: string; page?: number; limit?: number } = {},
) {
  const enabled = isCapabilityEnabled("browse");
  const search = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  const query = useQuery({
    queryKey: editorContextKeys.products(params),
    queryFn: () => fetcher<unknown>(search ? `/products?${search}` : "/products"),
    enabled,
    meta: { silent: true },
  });
  return { ...query, unavailable: !enabled };
}
